import { call, put, all, select, takeEvery, takeLatest } from "redux-saga/effects";
import { RootState } from "../../app/store";
import { COMMAND_API } from './commandAPI';
import { commandActions } from "./commandSlice";
import { missionActions } from "../mission/missionSlice";
import { replayActions } from "../replay/replaySlice";
import { toDate } from "../../utils/TimeUtils";

type WhatYouYield = any;
type WhatYouReturn = any;
type WhatYouAccept = any;

function* getCommands(action: { payload: any, type: string }): Generator<WhatYouYield, WhatYouReturn, WhatYouAccept> {
    try {
        yield put(commandActions.get_commands({}));
        const token: string = yield select((state: RootState) => state.auth.token);
        const missionId: string = yield select((state: RootState) => state.mission.active);

        let res: any;
        if (action.type === "replay/start") {
            res = yield call(COMMAND_API.getCommandsWithDate, token, missionId, action.payload.from, action.payload.to);
        } else {
            res = yield call(COMMAND_API.getCommands, token, missionId);
        };

        yield put(commandActions.get_commands_success({commands: res.data}));
    } catch (error: any) {
        console.log("Error in 'getCommandProcess' saga", error);
        yield put(commandActions.get_commands_failure({ status: error.response?.status, text: error.response?.statusText, data: error.response?.data }));
    }
};

function* watchGetCommands() {
    yield takeLatest([missionActions.set_active_mission, replayActions.stop], getCommands);
};

function* getCommandsHistory(action: { payload: any, type: string }): Generator<WhatYouYield, WhatYouReturn, WhatYouAccept> {
    const token: string = yield select((state: RootState) => state.auth.token);
    const missionId: string = yield select((state: RootState) => state.mission.active);
    const res: any = yield call(COMMAND_API.getCommandsHistory, token, missionId, action.payload.from, action.payload.to);

    /* Retrieve only the initial object for each command */
    const initialCommands = res.data.reduce((acc: any[], curr: any) => {
        const first = curr.history[0].content_object;
        if (first.created <= action.payload.from) acc.push(first);
        return acc;
    }, []);

    const history = res.data.map((action: any) => {
        let h: any = {};
        action.history.forEach((ac: any, i: number) => {
            h[ac.received] = ac.content_object
        });
        return h;
    });

    yield put(commandActions.get_commands_success({commands: initialCommands, history}));

};

function* watchReplayStart() {
    yield takeLatest((action: any) => (action.type === replayActions.start.type && !action.payload.fromFile), getCommandsHistory);
};

function* replayUpdate(action: { payload: any, type: string }): Generator<WhatYouYield, WhatYouReturn, WhatYouAccept> {
    const currentTime: string = yield select((state: RootState) => state.replay.currentTime);
    const history: any[] = yield select((state: RootState) => state.command.history);

    let commands = history.reduce((acc: any[], curr: any) => {
        const ret: any = Object.entries(curr).reverse().find(([time, el]: [string, any]) => {
            return (time < currentTime);
        });
        if (ret) acc.push(ret[1]);
        return acc;
    }, []);

    // Remove deleted commands from the list
    commands = commands.filter((cm: any) => {
        const deleteTS = toDate(cm.delete).getTime() + 3 * 60000; // We are adding 3 minutes to get the real delete TS because when deleting an object, we are removing 3 minutes.
        const currentTS = toDate(currentTime).getTime();
        return !(deleteTS <= currentTS);
    });

    yield put(commandActions.get_commands_success({commands}));
};

function* watchReplayUpdate() {
    yield takeLatest([replayActions.increment_time, replayActions.set_currentTime], replayUpdate);
};

function* sendRelayCommand(action: { payload: any, type: string }): Generator<WhatYouYield, WhatYouReturn, WhatYouAccept> {
    try {
        const token: string = yield select((state: RootState) => state.auth.token);
        const missionId: string = yield select((state: RootState) => state.mission.active);

        const res = yield call(COMMAND_API.sendRelayCommand, token, missionId, action.payload.destination, {relayId: action.payload.relayId, state: action.payload.state});

        yield put(commandActions.add_or_edit_command_success({
            id: res.data.id,
            type: res.data.payload.type,
            status: res.data.status,
            source: res.data.source,
            updated: res.data.last_update,
            parameters: res.data.payload
        }));
    } catch (error: any) {
        console.log("Error in 'sendRelayCommand' saga", error);
        yield put(commandActions.add_or_edit_command_failure({status: error.response?.status, text: error.response?.statusText, data: error.response?.data}));
    }
};

function* watchSendRelayCommand() {
    yield takeEvery(commandActions.send_relay_command, sendRelayCommand);
};

function* sendBuzzerCommand(action: { payload: any, type: string }): Generator<WhatYouYield, WhatYouReturn, WhatYouAccept> {
    try {
        const token: string = yield select((state: RootState) => state.auth.token);
        const missionId: string = yield select((state: RootState) => state.mission.active);

        const res = yield call(COMMAND_API.sendBuzzerCommand, token, missionId, action.payload.destination, {frequency: action.payload.frequency});

        yield put(commandActions.add_or_edit_command_success({
            id: res.data.id,
            type: res.data.payload.type,
            status: res.data.status,
            source: res.data.source,
            updated: res.data.last_update,
            parameters: res.data.payload
        }));
    } catch (error: any) {
        console.log("Error in 'sendBuzzerCommand' saga", error);
        yield put(commandActions.add_or_edit_command_failure({status: error.response?.status, text: error.response?.statusText, data: error.response?.data}));
    }
};

function* watchSendBuzzerCommand() {
    yield takeEvery(commandActions.send_buzzer_command, sendBuzzerCommand);
};

export default function* watchCommand() {
    yield all([
        watchGetCommands(),
        watchSendRelayCommand(),
        watchSendBuzzerCommand(),
        watchReplayStart(),
        watchReplayUpdate()
    ]);
};